import React, { useContext } from 'react';
import { FavoritesPillStrip, FavoritePill } from './FavoritesPillStrip';
import { GettyFavoritesContext } from '../../contexts/GettyFavoritesContext';
import type { VocabularyType } from '../../utils/gettyFavoritesApi';
import type {
  ObjectHierarchicalMetadataValue,
  ObjectMetadataValue,
} from '../../types/ObjectHierarchicalMetadataValue';

interface GettyFavoritesStripProps {
  vocabularyType: VocabularyType;
  /** Current picker value(s); used to mark a pill as already-selected. */
  currentValue: ObjectMetadataValue | ObjectMetadataValue[];
  /** Single (work-type) vs multi (techniques/materials/substrate). */
  multiple: boolean;
  /** Called with the new picker value when a pill is clicked. */
  onSelect: (value: ObjectMetadataValue | ObjectMetadataValue[]) => void;
  /** Optional override of the strip title; default "⭐ FAVORITES". */
  title?: string;
}

const buildValueFromFavorite = (favorite: {
  uri: string;
  textValue: string;
  breadcrumbs: string[];
}): ObjectHierarchicalMetadataValue => ({
  valueUri: favorite.uri,
  textValue: favorite.textValue,
  isFreetext: false,
  breadcrumbs: favorite.breadcrumbs,
});

const isHierarchical = (
  value: ObjectMetadataValue,
): value is ObjectHierarchicalMetadataValue => !value.isFreetext;

export const GettyFavoritesStrip: React.FC<GettyFavoritesStripProps> = ({
  vocabularyType,
  currentValue,
  multiple,
  onSelect,
  title = '⭐ FAVORITES',
}) => {
  // Read directly from context (rather than via useGettyFavorites) so the
  // strip silently renders nothing when no provider is mounted — keeps
  // tests that mount MediaTab without GettyFavoritesProvider working.
  const ctx = useContext(GettyFavoritesContext);
  if (!ctx) return null;
  const favorites = ctx.getFavoritesForType(vocabularyType);

  if (favorites.length === 0) return null;

  const currentArray = Array.isArray(currentValue)
    ? currentValue
    : currentValue
      ? [currentValue]
      : [];
  const activeUris = new Set(
    currentArray.filter(isHierarchical).map((v) => v.valueUri),
  );

  const pills: FavoritePill[] = favorites.map((fav) => ({
    id: fav.uri,
    label: fav.textValue,
    active: activeUris.has(fav.uri),
  }));

  const handleClick = (pill: FavoritePill) => {
    const favorite = favorites.find((f) => f.uri === pill.id);
    if (!favorite) return;
    const newValue = buildValueFromFavorite(favorite);

    if (!multiple) {
      onSelect(newValue);
      return;
    }

    const index = currentArray.findIndex(
      (v) => isHierarchical(v) && v.valueUri === favorite.uri,
    );
    const next =
      index >= 0
        ? currentArray.filter((_, i) => i !== index)
        : [...currentArray, newValue];
    onSelect(next);
  };

  const handleRemove = (pill: FavoritePill) => {
    const favorite = favorites.find((f) => f.uri === pill.id);
    if (!favorite) return;
    // toggleFavorite acts as remove when the URI is already a favorite
    ctx.toggleFavorite(
      vocabularyType,
      favorite.uri,
      favorite.textValue,
      favorite.breadcrumbs,
    );
  };

  return (
    <FavoritesPillStrip
      title={title}
      pills={pills}
      onPillClick={handleClick}
      onPillRemove={handleRemove}
    />
  );
};
